import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MDialog from "components/Shared/MDialog.tsx";
import { MSelect, MSpinner } from "components/Shared";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { getAssignableUsers } from "actions/qualityAssurance";
import { sendCases } from "requests/qualityAssurance/index.ts";
import responseHandler from "responseHandler";
import { updateQuickListInPlace } from "actions/homeowner/quickList";

const createQuicklistUpdate = (quickListId, cases, agentName) => {
  const update = cases.map(c => {
    return {
      controlIDNumber: c.controlIDNumber,
      qaAgent: agentName,
    };
  });
  return {
    id: quickListId,
    key: "controlIDNumber",
    update
  };
};

const QualityAssuranceAssign = ({
  getAssignableUsers,
  isFetchingAssignableUsers,
  assignableUsers,
  visible,
  hideDialog,
  selectedCases,
  makeToast,
  updateQuickList,
  selectedQuickListId,
}) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignToUsersList, setAssignToUsersList] = useState([]);
  const [assigningNote, setAssigningNote] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    return (() => { mounted.current = false; });
  }, []);

  useEffect(() => {
    if (assignableUsers.length === 0 && !isFetchingAssignableUsers) {
      getAssignableUsers();
    } else if (assignableUsers.length > 0) {
      const sortedAssignableUsers = assignableUsers.sort((a, b) => a.label.localeCompare(b.label));
      setAssignToUsersList(sortedAssignableUsers);
      setSelectedUser(sortedAssignableUsers[0]);
    }
  }, [assignableUsers]);

  const onSelectionChange = (option) => {
    setSelectedUser(option);
  };

  const getRequestBody = () => {
    const payload = selectedCases.map(c => {
      return {
        controlIdNumber: c.controlIDNumber,
        qaQueueId: c.qaQueueId,
        QaQueueTypeId: 1, // INSURANCE (currently only supporting this queue type)
        AssignToInternetUserId: selectedUser.value,
        Notes: assigningNote
      };
    });
    return payload;
  };

  const onSuccess = () => {
    const update = createQuicklistUpdate(selectedQuickListId, selectedCases, selectedUser.label);
    updateQuickList(update);
    makeToast("Cases assigned.", "success");
    finishAssigning();
  };

  const finishAssigning = () => {
    hideDialog();
    setIsAssigning(false);
    setAssigningNote("");
  };

  const onAssign = () => {
    setIsAssigning(true);
    const payload = getRequestBody();
    sendCases(payload, onSuccess, finishAssigning, mounted);
  };

  const getContentActions = () => {
    if (isAssigning) return [];
    const actions = [
      <Button key="cancelAssignQA" onClick={hideDialog} >Cancel</Button>
    ];
    if (selectedCases.length > 0) {
      actions.unshift(<Button key="assignQA" onClick={onAssign} >Assign</Button>);
    }
    return actions;
  };

  return (
    <MDialog
      id="assign-qa-agent-dialog"
      className="assign-qa-agent-dialog"
      title="Assign to QA Agent"
      contentClassName="assign-qa-agent-dialog-content"
      visible={visible}
      contentActions={getContentActions()}
    >
      {(isFetchingAssignableUsers || isAssigning) && <MSpinner />}
      {selectedCases.length === 0 && !isAssigning && <p>No cases selected. Please use the checkboxes to select cases to assign.</p>}
      {selectedCases.length > 0 && !isFetchingAssignableUsers && !isAssigning && <>
        <p>{`${selectedCases && selectedCases.length} ${selectedCases.length > 1 ? "cases" : "case"} selected. Choose a user to assign to:`}</p>
        <MSelect
          id="assign-qa-agent-mselect"
          className="assign-qa-agent-dropdown"
          isClearable={false}
          onChange={onSelectionChange}
          options={assignToUsersList}
          defaultValue={assignToUsersList[0]}
          getMenuPortalTarget={() => document.querySelector(".assign-qa-agent-dialog")}
        />
        <p>User must be mapped to show up in dropdown</p>
        <TextField
          variant="standard"
          label={"Note:"}
          placeholder={""}
          value={assigningNote}
          onChange={(e) => setAssigningNote(e.target.value)}
        />      
      </>}
    </MDialog>
  );
};

QualityAssuranceAssign.propTypes = {
  getAssignableUsers: PropTypes.func,
  isFetchingAssignableUsers: PropTypes.bool,
  assignableUsers: PropTypes.arrayOf(PropTypes.object),
  visible: PropTypes.bool,  // Show/hide dialog
  hideDialog: PropTypes.func, // Function to call to set dialog to hidden
  selectedCases: PropTypes.arrayOf(PropTypes.object), // Array of {controlIdNumber, qaQueueId}
  makeToast: PropTypes.func,
  updateQuickList: PropTypes.func,
  selectedQuickListId: PropTypes.number, // Id of the selected quicklist
};

QualityAssuranceAssign.defaultProps = {
  selectedCases: [],
  visible: false,
};

const mapStateToProps = state => {
  return {
    isFetchingAssignableUsers: state.qualityAssurance.isFetchingAssignableUsers,
    assignableUsers: state.qualityAssurance.assignableUsers,
  };
};

const mapDispatchToProps = dispatch => ({
  getAssignableUsers() {
    dispatch(getAssignableUsers());
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  updateQuickList(update) {
    dispatch(updateQuickListInPlace(update));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(QualityAssuranceAssign);